import { useMemo } from 'react';

import {
  ScreeningData,
  ScreeningV2Data as BackendScreeningV2Data,
} from '@/services/types/intake-types';

import { ScreeningV2Data } from './types';

// Intakes created on or after this date always use V2 screening
const V2_CUTOFF_DATE = '2026-02-05';

interface UseScreeningV2EnabledParams {
  /** The case type of the intake */
  caseType: string | null | undefined;
  /** V1 screening data from form_data.screening (for legacy cases) */
  existingScreeningData: ScreeningData | null | undefined;
  /** V2 screening data from form_data.screening_v2 (backend format or frontend format) */
  existingScreeningV2Data:
    | BackendScreeningV2Data
    | ScreeningV2Data
    | null
    | undefined;
  /** When the intake was created (ISO string) */
  intakeCreatedAt?: string | null;
}

interface UseScreeningV2EnabledResult {
  /** Whether V2 UI should be shown */
  isV2Enabled: boolean;
  /** Whether we should show the V1 UI (either disabled or has existing V1 data) */
  shouldShowV1: boolean;
  /** Reason for the decision (for debugging) */
  reason: string;
}

/**
 * Determines whether the V2 screening UI should be shown for an intake.
 *
 * Logic:
 * 1. If V2 data exists → show V2 (resuming existing V2 session)
 * 2. If intake was created on or after V2_CUTOFF_DATE → always show V2
 * 3. If V1 data exists → show V1 (legacy cases before cutoff)
 * 4. Otherwise → show V2 (new cases get V2 for all case types)
 */
export const useScreeningV2Enabled = ({
  caseType,
  existingScreeningData,
  existingScreeningV2Data,
  intakeCreatedAt,
}: UseScreeningV2EnabledParams): UseScreeningV2EnabledResult => {
  return useMemo(() => {
    // If V2 data exists, show V2 (resuming existing session)
    if (existingScreeningV2Data?.version === 'v2') {
      return {
        isV2Enabled: true,
        shouldShowV1: false,
        reason: 'Resuming existing V2 screening session',
      };
    }

    // If intake was created on or after cutoff date, always use V2
    if (intakeCreatedAt) {
      const createdDate = intakeCreatedAt.split('T')[0]; // Extract YYYY-MM-DD
      if (createdDate >= V2_CUTOFF_DATE) {
        return {
          isV2Enabled: true,
          shouldShowV1: false,
          reason: `Intake created on ${createdDate} (>= ${V2_CUTOFF_DATE}), using V2`,
        };
      }
    }

    // If V1 data exists, show V1 (legacy cases before cutoff)
    if (existingScreeningData && Object.keys(existingScreeningData).length > 0) {
      return {
        isV2Enabled: false,
        shouldShowV1: true,
        reason: 'Legacy case with existing V1 screening data',
      };
    }

    // New cases get V2 for all case types
    return {
      isV2Enabled: true,
      shouldShowV1: false,
      reason: `V2 screening enabled for case type '${caseType}'`,
    };
  }, [caseType, existingScreeningData, existingScreeningV2Data, intakeCreatedAt]);
};

export default useScreeningV2Enabled;
